/**
 * Frequently-bought-together bundle: selection, pricing, and add-to-cart.
 */
import { formatMoney } from '@theme/money-formatting';

(function () {
  const TAG_COLORS = ['blue', 'pink', 'orange'];
  const DEFAULT_BUNDLE_DISCOUNT_PERCENT = 35;
  const DEFAULT_BUNDLE_MIN_ITEMS = 2;

  function getBundleDiscountPercent(bundle) {
    const percent = Number(bundle.dataset.bundleDiscountPercent);
    return Number.isFinite(percent) && percent > 0 ? percent : DEFAULT_BUNDLE_DISCOUNT_PERCENT;
  }

  function getBundleMinItems(bundle) {
    const min = Number(bundle.dataset.bundleMinItems);
    return Number.isFinite(min) && min > 1 ? min : DEFAULT_BUNDLE_MIN_ITEMS;
  }

  function getCartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (item) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.push(item.dataset.sectionId);
      }
    });
    return ids;
  }

  function openCartDrawer() {
    function tryOpen() {
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }
      return false;
    }

    if (tryOpen()) return;

    customElements.whenDefined('cart-drawer-component').then(function () {
      tryOpen();
    });
  }

  function dispatchCartUpdate(cart, sourceId, sections) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: String(sourceId),
          data: {
            source: 'product-pixel-bundle',
            itemCount: cart.item_count,
            sections: sections,
          },
        },
      })
    );
  }

  function addItemsToCart(variantIds, triggerButton, options) {
    if (!variantIds.length) return Promise.resolve();

    options = options || {};
    const isBundleAdd = options.isBundleAdd === true;
    const sectionIds = getCartSectionIds();
    const payload = {
      items: variantIds.map(function (id) {
        const item = { id: Number(id), quantity: 1 };
        if (isBundleAdd) {
          item.properties = { _bundle: 'true' };
        }
        return item;
      }),
    };

    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const labelSpan = triggerButton instanceof HTMLButtonElement
      ? triggerButton.querySelector('[data-bundle-button-label]')
      : null;
    const originalLabel = labelSpan ? labelSpan.textContent : '';

    if (triggerButton instanceof HTMLButtonElement) {
      triggerButton.disabled = true;
      if (labelSpan && triggerButton.dataset.addingLabel) {
        labelSpan.textContent = triggerButton.dataset.addingLabel;
      }
    }

    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

    return fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) {
          throw new Error(data.message || data.description || 'Could not add to cart');
        }

        const minItems = Number(options.minItems) || DEFAULT_BUNDLE_MIN_ITEMS;
        const shouldApplyBundleDiscount = isBundleAdd && variantIds.length >= minItems;

        function finishCartUpdate(cart, sections) {
          dispatchCartUpdate(cart, variantIds[0], sections);
          openCartDrawer();
        }

        if (shouldApplyBundleDiscount && window.CartBundleDiscount) {
          return window.CartBundleDiscount.applyIfNeeded(sectionIds).then(function (result) {
            const sections = result && result.sections ? result.sections : data.sections;
            const cartPromise = result && result.cart
              ? Promise.resolve(result.cart)
              : fetch('/cart.js').then(function (response) {
                  return response.json();
                });

            return cartPromise.then(function (cart) {
              finishCartUpdate(cart, sections);
            });
          });
        }

        return fetch('/cart.js')
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            finishCartUpdate(cart, data.sections);
          });
      })
      .catch(function (error) {
        console.error(error);
      })
      .finally(function () {
        if (triggerButton instanceof HTMLButtonElement) {
          triggerButton.disabled = false;
          if (labelSpan && originalLabel) {
            labelSpan.textContent = originalLabel;
          }
        }
      });
  }

  function getSelectedCards(bundle) {
    return Array.from(bundle.querySelectorAll('[data-bundle-card].is-selected'));
  }

  function renderTag(card) {
    const title = card.dataset.title || '';
    const color = card.dataset.tagColor || TAG_COLORS[0];
    const tag = document.createElement('span');
    tag.className =
      'product-information--pixel__bundle-tag product-information--pixel__bundle-tag--' + color;
    tag.textContent = title;
    return tag;
  }

  function updateBundleSummary(bundle) {
    const selectedCards = getSelectedCards(bundle);
    const count = selectedCards.length;
    const moneyFormat = bundle.dataset.moneyFormat || '${{amount}}';
    const currency = bundle.dataset.currency || 'USD';

    const countEl = bundle.querySelector('[data-bundle-selected-count]');
    const tagsList = bundle.querySelector('[data-bundle-tags-list]');
    const totalEl = bundle.querySelector('[data-bundle-total-price]');
    const compareEl = bundle.querySelector('[data-bundle-compare-price]');
    const saveEl = bundle.querySelector('[data-bundle-save-line]');
    const addAllButton = bundle.querySelector('[data-bundle-add-all]');

    if (countEl) {
      if (count === 1) {
        countEl.textContent = bundle.dataset.selectedLabelOne || '1 item selected';
      } else {
        const template = bundle.dataset.selectedLabelOther || '{{ count }} items selected';
        countEl.textContent = template.replace('{{ count }}', String(count));
      }
    }

    if (tagsList) {
      tagsList.replaceChildren();
      selectedCards.forEach(function (card) {
        tagsList.appendChild(renderTag(card));
      });
    }

    let subtotalCents = 0;
    let compareCents = 0;

    selectedCards.forEach(function (card) {
      subtotalCents += Number(card.dataset.price) || 0;
      compareCents += Number(card.dataset.comparePrice) || Number(card.dataset.price) || 0;
    });

    const discountPercent = getBundleDiscountPercent(bundle);
    const minItems = getBundleMinItems(bundle);
    const bundleDiscountActive = count >= minItems;

    let displayCents = subtotalCents;
    let compareDisplayCents = 0;
    let savingsCents = 0;
    let savingsPercent = 0;

    if (bundleDiscountActive) {
      displayCents = Math.round(subtotalCents * (100 - discountPercent) / 100);
      compareDisplayCents = subtotalCents;
      savingsCents = subtotalCents - displayCents;
      savingsPercent = discountPercent;
    } else if (count > 0 && compareCents > subtotalCents) {
      displayCents = subtotalCents;
      compareDisplayCents = compareCents;
      savingsCents = compareCents - subtotalCents;
      savingsPercent = Math.round((savingsCents / compareCents) * 100);
    }

    if (totalEl) {
      totalEl.textContent = formatMoney(displayCents, moneyFormat, currency);
    }

    if (compareEl) {
      if (count > 0 && compareDisplayCents > displayCents) {
        compareEl.textContent = formatMoney(compareDisplayCents, moneyFormat, currency);
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }

    if (saveEl) {
      if (count > 0 && savingsCents > 0) {
        const template = bundle.dataset.saveLabel || 'Save {{ amount }} ({{ percent }}% off)';
        saveEl.textContent = template
          .replace('{{ amount }}', formatMoney(savingsCents, moneyFormat, currency))
          .replace('{{ percent }}', String(savingsPercent));
        saveEl.hidden = false;
      } else {
        saveEl.textContent = '';
        saveEl.hidden = true;
      }
    }

    if (addAllButton instanceof HTMLButtonElement) {
      addAllButton.disabled = count === 0;
    }
  }

  function setCardSelected(card, selected) {
    card.classList.toggle('is-selected', selected);

    const toggle = card.querySelector('[data-bundle-toggle]');
    if (toggle instanceof HTMLButtonElement) {
      toggle.setAttribute('aria-pressed', selected ? 'true' : 'false');
      toggle.setAttribute(
        'aria-label',
        selected ? toggle.dataset.deselectLabel || '' : toggle.dataset.selectLabel || ''
      );
    }
  }

  function initBundle(bundle) {
    if (!(bundle instanceof HTMLElement)) return;
    if (bundle.dataset.bundleReady === 'true') return;
    bundle.dataset.bundleReady = 'true';

    const cards = bundle.querySelectorAll('[data-bundle-card]');
    cards.forEach(function (card) {
      if (!(card instanceof HTMLElement)) return;

      setCardSelected(card, true);

      const toggle = card.querySelector('[data-bundle-toggle]');
      if (toggle instanceof HTMLButtonElement) {
        toggle.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          setCardSelected(card, !card.classList.contains('is-selected'));
          updateBundleSummary(bundle);
        });
      }

      const addItemButton = card.querySelector('[data-bundle-add-item]');
      if (addItemButton instanceof HTMLButtonElement) {
        addItemButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          const variantId = card.dataset.variantId;
          if (!variantId) return;
          addItemsToCart([variantId], addItemButton);
        });
      }
    });

    const addAllButton = bundle.querySelector('[data-bundle-add-all]');
    if (addAllButton instanceof HTMLButtonElement) {
      addAllButton.addEventListener('click', function () {
        const variantIds = getSelectedCards(bundle)
          .map(function (card) {
            return card.dataset.variantId;
          })
          .filter(Boolean);
        addItemsToCart(variantIds, addAllButton, {
          isBundleAdd: true,
          minItems: getBundleMinItems(bundle),
        });
      });
    }

    updateBundleSummary(bundle);
  }

  function initBundles() {
    document.querySelectorAll('[data-product-pixel-bundle]').forEach(initBundle);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundles);
  } else {
    initBundles();
  }

  document.addEventListener('shopify:section:load', initBundles);
})();
